import React from "react";
import { Crown } from "./CrownIcon";

const pillars = [
    {
        tone: "kickgreen",
        title: "Real Talk",
        body: "No filter. No fluff. Just lived-truth from Miami corners to NOLA pews.",
    },
    {
        tone: "hotpink",
        title: "Real Testimony",
        body: "Every night I share what God brought me through — and where He's taking us.",
    },
    {
        tone: "skyblue",
        title: "Real Loud",
        body: "Streamed past midnight 'cause that's when the realest conversations hit.",
    },
];

const toneCls = {
    kickgreen: { ring: "border-kickgreen", text: "text-kickgreen" },
    hotpink: { ring: "border-hotpink", text: "text-hotpink" },
    skyblue: { ring: "border-skyblue", text: "text-skyblue" },
};

export const AboutSection = () => {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute -top-20 left-1/2 -translate-x-1/2 w-[42rem] h-[42rem] rounded-full bg-hotpink/15 blur-3xl pointer-events-none"
            />
            <div
                aria-hidden
                className="absolute bottom-0 right-0 w-[32rem] h-[32rem] rounded-full bg-skyblue/15 blur-3xl pointer-events-none"
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                {/* Image */}
                <div className="lg:col-span-6 order-2 lg:order-1 relative">
                    <div
                        aria-hidden
                        className="absolute -top-6 -left-2 z-20 bg-skyblue text-black font-anton uppercase text-xs tracking-widest px-3 py-1 border-2 border-black rotate-[-5deg]"
                    >
                        Mic Drop · 1AM EST
                    </div>
                    <img
                        data-testid="about-portrait"
                        src="/preacher-about.png"
                        alt="Leonard the Preacher mid-sermon, Miami GTA-style"
                        loading="lazy"
                        className="relative z-10 w-full max-w-lg mx-auto border-4 border-black brutal-shadow-pink rotate-[2deg] hover:rotate-0 transition-transform duration-500"
                    />
                    <div
                        aria-hidden
                        className="absolute -bottom-5 right-6 z-20 bg-kickgreen text-black font-anton uppercase text-xs tracking-widest px-3 py-1 border-2 border-black rotate-[6deg]"
                    >
                        ✦ Live · On Kick
                    </div>
                </div>

                {/* Copy */}
                <div className="lg:col-span-6 order-1 lg:order-2">
                    <div className="inline-flex items-center gap-2 mb-4">
                        <Crown className="w-9 h-7" color="#FF2E93" />
                        <span className="font-anton uppercase tracking-[0.3em] text-xs text-hotpink">
                            Meet the Preacher
                        </span>
                    </div>
                    <h2
                        data-testid="about-title"
                        className="font-anton uppercase text-5xl md:text-7xl text-white leading-[0.9] stamp-sm mb-4"
                    >
                        Bibles in the
                        <br />
                        <span className="text-kickgreen">Bentley</span>.
                        <br />
                        Disciples in
                        <br />
                        the <span className="text-skyblue">streets</span>.
                    </h2>
                    <p className="font-graffiti text-hotpink text-3xl md:text-4xl -rotate-2 mb-3">
                        our mission ✝
                    </p>
                    <h3
                        data-testid="mission-title"
                        className="font-anton uppercase text-2xl md:text-3xl text-white leading-tight mb-5"
                    >
                        Taking the Gospel to the{" "}
                        <span className="text-kickgreen">Internet Street</span>
                    </h3>
                    <div
                        data-testid="mission-body"
                        className="font-chivo text-white/80 text-base leading-relaxed max-w-xl space-y-4 mb-8"
                    >
                        <p>
                            The mission of{" "}
                            <span className="text-skyblue font-bold">
                                leonardthepreacher.com
                            </span>{" "}
                            is to invade the digital frontline and preach the raw,
                            unfiltered truth of Jesus Christ to a lost streamer culture
                            and a generation drifting in darkness.
                        </p>
                        <p>
                            Gen Z is being sold a lie — told to live for the next party,
                            the next high, empty relationships, and fleeting internet
                            validation. But Christ didn't call us to sit comfortably in
                            pews; He commanded us to{" "}
                            <span className="text-kickgreen font-bold">
                                make disciples of all nations
                            </span>
                            . For us, that means hitting the biggest, loudest modern-day
                            intersection there is:{" "}
                            <span className="text-hotpink font-bold">
                                the internet street
                            </span>
                            .
                        </p>
                        <p>
                            We are claiming the streaming culture for the Kingdom of
                            God. In every chat, on every platform, and across every
                            stream, broken hearts will encounter the radical grace and
                            truth of Jesus. We aren't here to blend in — we are here to
                            disrupt the culture, break chains of addiction and identity
                            crisis, and build an uncompromising army of{" "}
                            <span className="text-skyblue font-bold">
                                digital disciples
                            </span>
                            .
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {pillars.map((p) => (
                            <div
                                key={p.title}
                                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`bg-black border-2 ${toneCls[p.tone].ring} p-4 hover:-translate-y-1 transition-transform`}
                            >
                                <div
                                    className={`font-anton uppercase tracking-wider text-xl ${toneCls[p.tone].text} leading-none mb-2`}
                                >
                                    {p.title}
                                </div>
                                <p className="font-chivo text-white/70 text-xs leading-relaxed">
                                    {p.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
